import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {logger} from '../utils/logger';
// Import the same localStorage key from SidebarContext to keep them in sync
import {SIDEBAR_STATE_KEY} from './SidebarContext';

/**
 * TopicListContext value type
 */
interface TopicListContextType {
  /** Whether the topic list is collapsed */
  isCollapsed: boolean;
  /** Toggle the collapse state */
  toggleCollapse: () => void;
  /** Expand the topic list */
  expandTopicList: () => void;
  /** Collapse the topic list */
  collapseTopicList: () => void;
}

const TopicListContext = createContext<TopicListContextType>({
  isCollapsed: false,
  toggleCollapse: () => {},
  expandTopicList: () => {},
  collapseTopicList: () => {},
});

/**
 * Provider component that makes topic list state available throughout the app
 * @param children - React children components
 */

export function TopicListProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage if available, otherwise default to false
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
    return savedState ? JSON.parse(savedState) : false;
  });

  // Add a global event listener for direct state forcing
  useEffect(() => {
    const handleForceCollapse = (event: CustomEvent) => {
      const { collapsed } = event.detail;
      logger.component('TopicListContext: Received force collapse event', { collapsed });
      setIsCollapsed(collapsed);
    };

    document.addEventListener('topic-list-collapse', handleForceCollapse as EventListener);

    return () => {
      document.removeEventListener('topic-list-collapse', handleForceCollapse as EventListener);
    };
  }, []);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    logger.component('Toggling topic list collapse state', {
      wasCollapsed: isCollapsed,
      willBeCollapsed: newState,
      source: 'TopicListContext.toggleCollapse',
    });
    setIsCollapsed(newState);
  };

  const expandTopicList = () => {
    logger.component('Expanding topic list', {
      wasCollapsed: isCollapsed,
      source: 'TopicListContext.expandTopicList',
    });

    // Toggle instead of setting directly to handle potential race conditions
    setIsCollapsed(prev => {
      // Only update if it's already collapsed
      if (prev) {
        logger.component('Setting topic list state to expanded');
        return false;
      }
      logger.component('Topic list was already expanded, no change made');
      return prev;
    });
  };

  const collapseTopicList = () => {
    logger.component('Collapsing topic list', {
      wasCollapsed: isCollapsed,
      source: 'TopicListContext.collapseTopicList',
    });

    // Toggle instead of setting directly to handle potential race conditions
    setIsCollapsed(prev => {
      // Only update if it's not already collapsed
      if (!prev) {
        logger.component('Setting topic list state to collapsed');
        return true;
      }
      logger.component('Topic list was already collapsed, no change made');
      return prev;
    });
  };

  return (
    <TopicListContext.Provider
      value={{ isCollapsed, toggleCollapse, expandTopicList, collapseTopicList }}
    >
      {children}
    </TopicListContext.Provider>
  );
}

/**
 * Hook for accessing topic list state throughout the app
 * @returns Object containing the current topic list state and methods to control it
 */
export const useTopicList = () => useContext(TopicListContext);
